import React, { useState } from "react";
import HeroLayout from "../components/HeroLayout";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneNumberField from "../components/PhoneNumberField";

const images = require.context("../assets/images", false);

const itineraryDays = [
  {
    day: "Day 1",
    title: "Arrive in Delhi",
    image: "./Delhi2.jpeg",
    text: "Welcome to Incredible India! On arrival, our driver will be waiting to transfer you directly to your hotel. Once checked in, the rest of the day is yours to unwind.",
    highlights: ["Airport Pickup"],
    meals: "None",
  },
  {
    day: "Day 2",
    title: "Delhi",
    image: "./Delhi3.jpeg",
    text: "Delhi is a living mosaic of history, flavour, and culture. Your morning begins in the atmospheric lanes of Old Delhi — take a rickshaw through the bustling corridors of Chandni Chowk, India's legendary food capital. Indulge in the city's iconic street food before moving on to the serene grandeur of Humayun's Tomb and the gleaming Bangla Sahib Gurudwara. The afternoon takes you to the awe-inspiring Qutub Minar — the world's tallest brick minaret. Drive past India Gate, the Secretariat, and Rashtrapati Bhavan. Your evening is yours to explore at your own pace.",
    highlights: ["Old Delhi (Chandni Chowk rickshaw ride)", "Humayun's Tomb", "Bangla Sahib Gurudwara", "Qutub Minar", "India Gate drive-past"],
    meals: "Breakfast",
    optional: "Old Delhi Food Walk OR Cook-n-dine with an Indian family",
  },
  {
    day: "Day 3",
    title: "Delhi - Agra",
    image: "./Golden-Traingle-shortTour-India.jpg",
    text: "After an early breakfast, your driver takes you on a smooth journey to Agra — the city of the Taj Mahal. Check in to your hotel, then head out to explore the magnificent Agra Fort, a stunning remnant of a bygone era. As the day winds down, make your way to Mehtab Bagh, where the sun sets behind the Taj Mahal with the Yamuna flowing gently below — a view you will never forget.",
    highlights: ["Agra Fort", "Mehtab Bagh — sunset view of the Taj Mahal"],
    meals: "Breakfast",
    optional: "2-hour train journey from Delhi to Agra to experience Indian railways",
  },
  {
    day: "Day 4",
    title: "Agra - Jaipur",
    image: "./frg.webp",
    text: "Begin the morning with a sunrise visit to the Taj Mahal — one of the Seven Wonders of the World, built by Emperor Shah Jahan in memory of his beloved wife Mumtaz Mahal. Best seen in the soft golden light of dawn. Return to the hotel for breakfast, then set off on the drive to Jaipur. En route, stop at the fascinating Abhaneri Stepwell. By the time you arrive in Jaipur — the Pink City and a proud UNESCO World Heritage Site — you will already have had a day full of wonders. Check in and relax.",
    highlights: ["Taj Mahal (Sunrise visit)", "Fatehpur Sikri", "Abhaneri Stepwell"],
    meals: "Breakfast",
    optional: "Traditional Rajasthani dinner with folk dance performance",
  },
  {
    day: "Day 5",
    title: "Jaipur",
    image: "./cameljaipur.jpg",
    text: "Begin your day at the breathtaking Amer Fort, a magnificent hilltop fortress blending Rajput and Mughal architecture. Explore the City Palace — a sprawling royal complex housing museums and stunning artefacts. Nearby, marvel at Jantar Mantar, an 18th-century astronomical observatory. Drive past the iconic Hawa Mahal — the Palace of Winds — famous for its 953 intricately carved windows. In the afternoon, explore Jaipur's vibrant local markets, renowned for colourful textiles, gemstones, and traditional handicrafts. End the day at the serene Birla Temple, beautifully illuminated in the evening.",
    highlights: ["Amer Fort", "City Palace", "Jantar Mantar", "Hawa Mahal", "Local Markets", "Birla Temple / Galta Ji Temple"],
    meals: "Breakfast",
  },
  {
    day: "Day 6",
    title: "Jaipur - Rawla Narlai",
    image: "./Rawla_Narlai.webp",
    marker: "",
    text: [
      "After breakfast, check out of your Jaipur hotel and embark on a scenic 5-hour drive through the rugged Rajasthani countryside to the hidden gem of Rawla Narlai - a beautifully restored 17th-century royal hunting lodge set among giant granite rocks. On arrival, check in and take the remainder of the afternoon at leisure - soak in the tranquillity and unique atmosphere of this intimate heritage property. As evening falls, join an optional guided village walk to experience the authentic rhythms of rural Rajasthan, interact with the warm local community, and gain a deeper insight into the traditions and daily life of this remarkable region.",
    ],
    highlights: ["Scenic countryside drive", "Rawla Narlai heritage stay", "Rural Rajasthan village experience"],
    driveDuration: "Approx. 5 hours",
    meals: "Breakfast",
    optional: "",
  },
  {
    day: "Day 7",
    title: "Narlai - Ranakpur - Udaipur",
    image: "./Udaipur.jpg",
    text: [
      "Rise early for an exhilarating morning leopard safari in the rocky outcrops surrounding Narlai - one of the few places in Rajasthan where leopards are regularly spotted. After the safari, return to the lodge for breakfast.",
      "Check out and set off on the drive to Udaipur. En route, make a memorable stop at the spectacular Ranakpur Jain Temple - one of the largest and most important Jain temples in India, renowned for its 1,444 intricately carved marble pillars, no two of which are alike. Continue onward to Udaipur - the City of Lakes - and check in to your hotel. The evening is at leisure. Explore the lakeside promenade, browse the bazaars, or simply enjoy the magical ambience of this romantic city.",
    ],
    highlights: ["Morning leopard safari in Narlai", "Ranakpur Jain Temple (en route)"],
    meals: "Breakfast",
  },
  {
    day: "Day 8",
    title: "Udaipur Sightseeing",
    image: "./Udaipur-Sightseeing.jpeg",
    text: [
      "Spend the day exploring the romantic and regal city of Udaipur at a relaxed pace. Visit the magnificent City Palace - the largest palace complex in Rajasthan - with sweeping views over Lake Pichola. Stroll through the old city, explore the Jagdish Temple, and browse the colourful bazaars filled with miniature paintings, silverware, and traditional textiles.",
      "As the sun begins to set, enjoy a serene boat ride on the shimmering waters of Lake Pichola, with the iconic Lake Palace floating dreamlike in the middle of the lake.",
    ],
    highlights: ["City Palace, Udaipur", "Jagdish Temple", "Old City bazaars", "Evening boat ride on Lake Pichola"],
    meals: "Breakfast",
  },
];

const extensions = [
  {
    title: "Option 1: Return to Delhi",
    duration: "10 Nights / 11 Days",
    image: "./return-delhi.jpg",
    days: [
      {
        day: "Day 9",
        title: "Udaipur - Delhi (Flight)",
        text: [
          "Enjoy a relaxed morning - treat yourself to a spa session at your hotel or spend time browsing Udaipur's charming bazaars for last-minute souvenirs and mementos of Rajasthan. Then transfer to Udaipur Airport for your flight to Delhi.",
          "On arrival in Delhi, check in to your hotel. The rest of the day is completely at leisure - unwind, explore a neighbourhood, or enjoy a fine dining experience in the capital.",
        ],
        meals: "Breakfast",
        optional: "Spa treatment or shopping in Udaipur",
      },
      {
        day: "Day 10",
        title: "Depart Delhi",
        text: "After breakfast, check out from your hotel. Our driver will transfer you to Delhi International Airport for your onward international flight. Our services conclude here - we hope you carry with you unforgettable memories of Incredible India!",
        meals: "Breakfast",
      },
    ],
  },
  {
    title: "Option 2: Udaipur - Varanasi Extension",
    duration: "13 Nights / 14 Days",
    image: "./Varanasi-extension.jpg",
    days: [
      {
        day: "Day 9",
        title: "Udaipur - Varanasi (Flight)",
        text: "Morning transfer to Udaipur Airport for your flight to Varanasi - one of the world's oldest living cities and the spiritual heart of India. On arrival, you will be transferred to your hotel. The rest of the day is at leisure; allow yourself time to relax and acclimatise to the mystical atmosphere of this sacred city on the banks of the River Ganges.",
        meals: "Breakfast",
      },
      {
        day: "Day 10",
        title: "Varanasi Sightseeing",
        text: [
          "Rise before dawn for a soul-stirring morning boat ride on the Ganges - one of the most profound experiences India has to offer. Drift past the ancient ghats as priests perform the morning prayers and rituals, and witness thousands of devotees beginning their day with sacred ablutions in the holy river.",
          "After breakfast, embark on a comprehensive city and temple sightseeing tour - visit the revered Kashi Vishwanath Temple (Golden Temple), Sankat Mochan Hanuman Temple, and the sacred ghats, each with its own unique character and story.",
          "In the afternoon, take an optional guided food walk through the atmospheric lanes of Varanasi - sample iconic street foods including kachori sabzi, lassi, thandai, and the legendary Banarasi paan.",
          "As evening descends, proceed to the Dashashwamedh Ghat for the magnificent Ganga Aarti - a breathtaking fire ceremony performed daily by priests, accompanied by chanting, bells, and incense. You will have pre-assigned seats for a front-row experience.",
        ],
        highlights: [
          "Morning boat ride on the Ganges",
          "Morning prayers and ritual observation on the ghats",
          "City & temple sightseeing (Kashi Vishwanath, Sankat Mochan)",
          "Ganga Aarti at Dashashwamedh Ghat (pre-assigned seats)",
        ],
        meals: "Breakfast",
        optional: "Guided Varanasi food walk",
      },
      {
        day: "Day 11",
        title: "Varanasi - Delhi (Flight)",
        text: "After breakfast, transfer to Varanasi Airport for your flight back to Delhi. On arrival in Delhi, check in to your hotel. The evening is at leisure - use the time for any last-minute shopping, a stroll through a local market, or a relaxed dinner.",
        meals: "Breakfast",
      },
      {
        day: "Day 12",
        title: "Depart Delhi",
        text: "After breakfast, check out and transfer to Delhi International Airport for your onward international flight. Our services conclude here - we trust your journey through Incredible India has been truly unforgettable.",
        meals: "Breakfast",
      },
    ],
  },
  {
    title: "Option 3: Udaipur - Kochi & Kerala Extension",
    duration: "14 Nights / 15 Days",
    image: "./Kochi&Kerala.jpg",
    days: [
      {
        day: "Day 9",
        title: "Udaipur - Kochi (Flight)",
        text: [
          "Transfer to Udaipur Airport for your flight to Kochi - the Queen of the Arabian Sea - your gateway to the lush landscapes and tranquil backwaters of Kerala, India's southernmost state.",
          "On arrival in Kochi, check in to your hotel and ease into a relaxed evening. Explore the charming heritage streets of Fort Kochi at your own pace.",
        ],
        meals: "Breakfast",
        optional: "Kathakali classical dance performance in Kochi - a vibrant and ancient art form of Kerala featuring elaborate costumes, vivid make-up, and expressive storytelling through dance",
      },
      {
        day: "Day 10",
        title: "Kochi Sightseeing & Drive to Marari Beach",
        text: [
          "Spend the morning exploring the historic sights of Kochi - stroll past the iconic Chinese Fishing Nets, visit the Dutch Palace (Mattancherry Palace) with its stunning Kerala murals, explore the ancient Jewish Synagogue, and wander through the spice markets and antique lanes of Fort Kochi.",
          "In the afternoon, drive to Marari Beach - one of Kerala's most pristine and peaceful stretches of coastline. Check in to your beach resort and spend the rest of the day unwinding by the Arabian Sea. Begin your wellness journey with restorative Ayurvedic treatments tailored to your individual needs.",
        ],
        highlights: [
          "Chinese Fishing Nets, Fort Kochi",
          "Dutch Palace (Mattancherry Palace)",
          "Jewish Synagogue & Spice Markets",
          "Drive to Marari Beach",
          "Ayurvedic wellness treatments",
        ],
        meals: "Breakfast",
      },
      {
        day: "Day 11",
        title: "Marari Beach - Rest & Wellness",
        text: "A full day of rest, rejuvenation, and wellness at Marari Beach. Surrender to the healing rhythms of traditional Kerala Ayurveda - choose from a range of authentic treatments including Abhyanga (full-body oil massage), Shirodhara (warm oil poured over the forehead), or Pizhichil (therapeutic oil bath). Alternatively, simply relax on the beach, swim in the sea, or explore the charming fishing village of Mararikulam on foot.",
        meals: "Breakfast",
      },
      {
        day: "Day 12",
        title: "Marari Beach - Rest & Wellness",
        text: "Another day of total relaxation by the sea. Continue your Ayurvedic wellness programme, take sunrise yoga on the beach, go for an early morning cycle through the coconut groves and paddy fields of this unspoilt coastal village, or simply enjoy the peace and beauty of this idyllic setting.",
        meals: "Breakfast",
      },
      {
        day: "Day 13",
        title: "Drive to Kochi - Fly Home",
        text: [
          "After a leisurely breakfast, check out from your beach resort and drive back to Kochi (approximately 1.5 hours). From Kochi, you have two options for your onward journey:",
          "Fly directly home from Cochin International Airport (subject to availability of international connections), or take a connecting flight to Delhi and depart on your international flight from Delhi.",
          "Our services conclude here. Thank you for choosing this extraordinary journey through Incredible India.",
        ],
        driveDuration: "Approx. 1.5 hours",
        meals: "Breakfast",
      },
    ],
  },
];

const inclusions = [
  "Accommodation in selected or similar 5-star / heritage hotels on double/twin sharing",
  "Buffet/set meals as per itinerary (Breakfast daily)",
  "Air-conditioned Toyota Innova Crysta / Hycross with driver for all intercity transfers and sightseeing",
  "Airport pickups and drops",
  "English-speaking expert city guides at each destination",
  "Monument and temple entrance fees as per itinerary",
  "Rickshaw ride in Old Delhi or Jaipur",
  "Morning leopard safari in Narlai (Day 7)",
  "Evening boat ride on Lake Pichola, Udaipur (Day 8)",
  "Ganga Aarti pre-assigned seats in Varanasi (Option 2 only)",
  "Complimentary amenities: mineral water and snack basket throughout",
  "All applicable taxes",
  "24 x 7 assistance throughout the tour",
];

const exclusions = [
  "International and domestic airfares",
  "Visa fees (required for India)",
  "Travel Insurance",
  "Optional activities and experiences listed in the itinerary",
  "Personal expenses — laundry, mini-bar, telephone, room service, etc.",
  "Anything not specified under Inclusions above",
];

function BestLuxuryPrivateIndiaTour() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+91",
    phone: "",
    message: "",
    tour: "Best of India Tour",
  });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setErrors({
      ...errors,
      [e.target.name]: "",
    });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[0-9]{7,15}$/.test(formData.phone)) {
      newErrors.phone = "Enter valid phone number";
    }
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    if (!captchaToken) {
      setErrors({ captcha: "Please verify captcha" });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch("https://www.luxurygoldentriangle.com/bkadmn/wp-json/custom/v1/contact_tour", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          phone: `${formData.countryCode} ${formData.phone}`.trim(),
          captcha: captchaToken,
        }),
      });
      const data = await response.json();

      if (data.success) {
        setSuccess("Message sent successfully!");
        setErrors({ captcha: "" });
        setFormData({
          name: "",
          email: "",
          countryCode: "+91",
          phone: "",
          message: "",
          tour: "Best of India Tour",
        });
      } else {
        setSuccess(data.message || "Something went wrong.");
      }
    } catch (error) {
      setSuccess("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderParagraphs = (content, className = "mb-3 font-14") => {
    const paragraphs = Array.isArray(content) ? content : [content];

    return paragraphs.map((paragraph, index) => (
      <p className={className} key={`${paragraph}-${index}`}>
        {paragraph}
      </p>
    ));
  };

  return (
    <>
      <HeroLayout>
        <div
          className="banner-bg standard-tour-hero bg-postion-top"
          style={{
            backgroundImage: `linear-gradient(#370b2e66, #370b2e66), url(${images("./aboutus_tour.webp")})`,
          }}
        >
          <div className="container">
            <div className="inner-banner-content full-width center color-white mt-5">
              <h1>Best of India Tour</h1>
            </div>
          </div>
        </div>
      </HeroLayout>

      <div className="tour-details mt-80">
        <div className="container">
          <div className="mb-5">
            <div className="tour-highlight-summary standard-highlight-layout">
              <div className="tour-highlights-card">
                <h2>Tour Highlights</h2>
                <ul>
                  <li><span aria-hidden="true">{"\u2605"}</span>Golden Triangle with Rajasthan beyond Jaipur</li>
                  <li><span aria-hidden="true">{"\u2605"}</span>Sunrise Taj Mahal and 8 UNESCO heritage experiences</li>
                  <li><span aria-hidden="true">{"\u2605"}</span>Rawla Narlai heritage stay with leopard safari</li>
                  <li><span aria-hidden="true">{"\u2605"}</span>Romantic Udaipur with Lake Pichola boat ride</li>
                  <li><span aria-hidden="true">{"\u2605"}</span>Optional Varanasi or Kerala extension</li>
                  <li><span aria-hidden="true">{"\u2605"}</span>Private car, guides, and personalised support</li>
                </ul>
              </div>
              <div className="tour-highlights-cardbkgrey">
                <h2>Tour Details</h2>
                <p><b>Cities</b>: Delhi &bull; Agra &bull; Jaipur &bull; Rawla Narlai &bull; Udaipur + Extension</p>
                <p><b>Duration</b>: 9-14 Nights / 10-15 Days (depending on extension chosen)</p>
                <p><b>Best Time to Visit</b>: September - March</p>
                <p><b>Tour Type</b>: Private & Luxury</p>
                <p><b>Customizable</b>: Yes - Tailored to Your Preferences</p>
                <p><b>Ideal For</b>: Couples, Families & Small Private Groups</p>
                <p><b>Language</b>: English, Hindi, Spanish, German, Italian, French, Japanese, Chinese</p>
              </div>
            </div>
          </div>

          <h2 className="mb-4">Itinerary</h2>
          {itineraryDays.map((item) => (
            <React.Fragment key={item.day}>
              {item.marker && <h3 className="extended-itinerary-marker">{item.marker}</h3>}
              <div className="tour-details-card mb-4">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <div className="tour-details-card-img">
                      <img src={images(item.image)} alt={item.title} />
                      <span>{item.day}</span>
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="border-bottom-card">
                      <h3>{item.title}</h3>
                      {renderParagraphs(item.text)}
                      <ul className="mb-3">
                        {item.highlights.map((highlight) => (
                          <li key={highlight}>{highlight}</li>
                        ))}
                      </ul>
                      {item.driveDuration && <p className="colr-prim mb-3">Drive Duration: {item.driveDuration}</p>}
                      <p className="colr-prim">Meals: {item.meals}</p>
                      {item.optional && <p className="colr-prim mb-3">Optional Experience: {item.optional}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}

          <h2 className="mb-4 mt-5">Extension Options From Udaipur</h2>
          <p className="mb-4">Choose one of the following three onward extensions from Udaipur:</p>
          <div className="row">
            {extensions.map((extension) => (
              <div className="col-md-12 col-lg-4" key={extension.title}>
                <div className="tour-details-card best-india-extension-card mb-4">
                  <img src={images(extension.image)} alt={extension.title} />
                  <div className="p-4">
                    <h3>{extension.title}</h3>
                    <p className="colr-prim mb-3">{extension.duration}</p>
                    <div className="extension-day-list">
                      {extension.days.map((day) => (
                        <div className="extension-day" key={`${extension.title}-${day.day}`}>
                          <h4>{day.day}: {day.title}</h4>
                          {renderParagraphs(day.text, "mb-2 font-14")}
                          {day.highlights && (
                            <ul className="mb-2">
                              {day.highlights.map((highlight) => (
                                <li key={highlight}>{highlight}</li>
                              ))}
                            </ul>
                          )}
                          {day.driveDuration && <p className="colr-prim mb-2">Drive Duration: {day.driveDuration}</p>}
                          <p className="colr-prim mb-2">Meals: {day.meals}</p>
                          {day.optional && <p className="colr-prim mb-0">Optional: {day.optional}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row tour-summary-row">
            <div className="col-md-12 col-lg-6">
              <div className="tour-details-card tour-summary-left-card mb-4 py-5 px-4">
                <h2 className="mb-3">Inclusions</h2>
                <ul className="tick-list">
                  {inclusions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-md-12 col-lg-6">
              <div className="tour-details-card mb-4 py-5 px-4">
                <h2 className="mb-3">Exclusions</h2>
                <ul className="dot-list-card">
                  {exclusions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="tour-details-card mb-4 py-5 px-4">
                <h2 className="mb-3">Hotels &amp; Transport</h2>
                <p>
                  We recommend and use 5-star hotels — which may be a standard 5-star, a luxury 5-star, or a heritage property such as Rawla Narlai. For vehicles, we use the Toyota Innova Crysta as standard; upgrades are available on request. Please ask your travel consultant for a full list of recommended hotels at each destination. You can check our{" "}
                  <Link to="/5-star-luxury-hotels">recommended luxury hotels</Link> and{" "}
                  <Link to="/car-details">available cars/vehicles</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tour-form mt-80" id="enquire">
        <img className="top-left-position" src={images("./dance-man.svg")} alt="" />
        <img className="bottom-right-position" src={images("./peacock.svg")} alt="" />
        <div className="container">
          <div className="form-card">
            <div className="heading center">
              <h2>Enquire Now</h2>
              <p>Share your details and our expert team will contact you with a private Best of India tour proposal.</p>
            </div>
            <form onSubmit={handleSubmit} noValidate>
              <div className="row">
                <div className="col-md-12 col-lg-6">
                  <div className="form-input-group mb-4">
                    <label htmlFor="best-india-name">Name<span>*</span></label>
                    <input id="best-india-name" type="text" className="form-control" placeholder="Enter your name" name="name" value={formData.name} onChange={handleChange} />
                    {errors.name && <p className="error">{errors.name}</p>}
                  </div>
                </div>
                <div className="col-md-12 col-lg-6">
                  <div className="form-input-group mb-4">
                    <label htmlFor="best-india-email">Email<span>*</span></label>
                    <input id="best-india-email" type="email" className="form-control" placeholder="Enter your email" name="email" value={formData.email} onChange={handleChange} />
                    {errors.email && <p className="error">{errors.email}</p>}
                  </div>
                </div>
                <div className="col-md-12 col-lg-6">
                  <PhoneNumberField countryCode={formData.countryCode} value={formData.phone} onChange={handleChange} error={errors.phone} />
                </div>
                <div className="col-md-12 col-lg-6 messagefield">
                  <div className="form-input-group mb-2">
                    <label htmlFor="best-india-message">Message<span>*</span></label>
                    <textarea id="best-india-message" name="message" className="form-control" placeholder="Please share your travel dates, preferred extension, travelers, hotel preferences, and any special requirements." value={formData.message} onChange={handleChange} />
                    {errors.message && <p className="error">{errors.message}</p>}
                  </div>
                </div>
                <div className="col-md-12">
                  <ReCAPTCHA sitekey="6Ld4kHosAAAAAKsayfQ1saymv6SeauUSxkHSn55b" onChange={(token) => setCaptchaToken(token)} />
                  {errors.captcha && <p className="error">{errors.captcha}</p>}
                </div>
              </div>
              <div className="form-btn center mt-3">
                <button type="submit" disabled={loading} className="primary-btn">
                  {loading ? "Sending..." : "Submit"} <img src={images("./arrow-right-btn.svg")} alt="Arrow" />
                </button>
                {success && <p className="success">{success}</p>}
                <p className="center mt-2">
                  Not your preferred tour? Click here for a{" "}
                  <Link to="/luxury-golden-triangle-enquiry" className="tailor-link">
                    <img src={images("./11.png")} alt="" />
                    Tailor Made Tour
                  </Link>.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default BestLuxuryPrivateIndiaTour;
